import { useState, useRef, useCallback, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  pointerWithin,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  useDraggable,
  useDroppable,
  DragOverlay,
  useDndMonitor,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Combobox, type ComboboxOption } from '../../components/ui/combobox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Search, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Plus, GripVertical, Calculator, Key, Copy, Link2, Settings, Trash2, Sparkles, BarChart3, GitBranch, X, Filter, Merge, AlertTriangle, Group, Unplug, Table2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { type PipelineNode, type PipelineNodeField, type PipelineNodeMapOption, type SingleRecordResponse, type DedupeRule } from '../../services/pipelineNode.service';
import { MultiSelectCombobox } from '../../components/ui/multi-select-combobox';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toastService } from '../../services/toast.service';
import { type PipelineNodeRelationship } from '../../hooks/usePipelineNodeRelationships';
import { getForeignKeyDisplayName } from '../../utils/foreignKeyUtils';
import { FilterTable, type FilterEntry } from '../../components/FilterTable';
import type { DataWhitelist, DataWhitelistEntry } from '../../components/UpstreamFilterTable';
import ApiService from '../../services/api.service';
import { formatPercentage } from '../../services/formatting.service';
import { ColumnLineageGraph, type ColumnLineageGraph as ColumnLineageGraphType } from '../../components/ColumnLineageGraph';
import { FullScreenTakeover } from '../../components/FullScreenTakeover';
import { NodeDataTable } from '../../components/NodeDataTable';
import { useColumnValueDistribution } from '../../hooks/useColumnValueDistribution';

// Column stats type
interface ColumnStats {
  key: string;
  distinct_values: number;
  empty_values: number;
  uniqueness: number;
  density: number;
  samples: any[];
  enhanced?: boolean;
}


// Helper function to generate a stable key for map options
function getMapOptionKey(mapOption: PipelineNodeMapOption): string {
  if (mapOption.id) {
    return mapOption.id;
  }
  return `${mapOption.source_node_id || 'unknown'}:${mapOption.attribute_id || mapOption.attribute_key || 'unknown'}`;
}

// Helper function to count mapped columns for a field
function countMappedColumns(field: NonNullable<PipelineNode['fields']>[0]): number {
  if (!field.map_options || field.map_options.length === 0) {
    return 0;
  }
  
  return field.map_options.reduce((count, option) => {
    if (option.sub_options && option.sub_options.length > 0) {
      return count + option.sub_options.filter(
        (sub) => sub.source_node_id && (sub.attribute_key || sub.attribute_id)
      ).length;
    }
    if (option.source_node_id && (option.attribute_key || option.attribute_id)) {
      return count + 1;
    }
    return count;
  }, 0);
}

// Helper function to get the display name for a field
function getFieldName(
  field: NonNullable<PipelineNode['fields']>[0],
  getRelationship?: (relationshipId: string) => any,
  getNodeName?: (nodeId: string) => string | undefined,
  currentNodeId?: string,
  pendingRelationships?: Map<string, Partial<PipelineNodeRelationship>>
): string {
  if (field.type === 'FOREIGN_KEY') {
    const displayName = getForeignKeyDisplayName(
      field as PipelineNodeField,
      getRelationship,
      getNodeName,
      pendingRelationships
    );
    if (displayName) {
      return displayName;
    }
  }
  return field.name || field.label || 'Unnamed Column';
}

// Custom collision detection
function customCollisionDetection(args: any) {
  const activeData = args.active.data.current;
  if (activeData?.type === 'source-field') {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
      return pointerCollisions;
    }
    return [];
  }
  return closestCenter(args);
}

// Helper function to check if a field cannot accept source column mappings
function cannotAcceptMappings(field: NonNullable<PipelineNode['fields']>[0], isCalculated: boolean): boolean {
  return isCalculated || field.type === 'DENORMALIZED' || field.type === 'FOREIGN_KEY';
}

// Helper function to get record count
function getRecordCount(node: PipelineNode): number | undefined {
  return node.shape?.total_records ?? node.total_records;
}

// Component to monitor drag events
function DragMonitor({ 
  setHoveredDropTarget, 
  workingCopy 
}: { 
  setHoveredDropTarget: (target: string | null) => void;
  workingCopy: PipelineNode | null;
}) {
  useDndMonitor({
    onDragOver: (event) => {
      if (event.active.data.current?.type === 'source-field') {
        if (event.over) {
          const overId = typeof event.over.id === 'string' ? event.over.id : String(event.over.id);
          if (overId.startsWith('drop-target-')) {
            setHoveredDropTarget(overId);
          } else {
            const fieldId = overId;
            const field = workingCopy?.fields?.find(
              (f) => (f.id || f.name || '') === fieldId
            );
            if (field) {
              setHoveredDropTarget(`drop-target-${fieldId}`);
            } else {
              setHoveredDropTarget(null);
            }
          }
        } else {
          setHoveredDropTarget(null);
        }
      } else {
        setHoveredDropTarget(null);
      }
    },
    onDragEnd: () => {
      setHoveredDropTarget(null);
    },
  });
  
  return null;
}

// Draggable source field component
interface DraggableSourceFieldProps {
  nodeId: string;
  field: NonNullable<PipelineNode['fields']>[0];
  mappedFieldCount: number;
  onBadgeClick: () => void;
  onViewStats: (nodeId: string, field: NonNullable<PipelineNode['fields']>[0]) => void;
  onViewLineage: (nodeId: string, field: NonNullable<PipelineNode['fields']>[0]) => void;
}

function DraggableSourceField({ nodeId, field, mappedFieldCount, onBadgeClick, onViewStats, onViewLineage }: DraggableSourceFieldProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `source-field-${nodeId}-${field.id || field.name}`,
    data: {
      type: 'source-field',
      nodeId,
      fieldId: field.id || field.name,
      fieldName: field.name || field.label,
      fieldType: field.type,
      attributeId: field.id || field.name,
      attributeKey: field.name || field.label,
    },
  });

  return (
    <div
      ref={setNodeRef}
      style={{ 
        visibility: isDragging ? 'hidden' : 'visible',
      }}
      className="px-3 py-2 bg-muted rounded-md text-sm"
    >
      <div className="flex items-center justify-between gap-2">
        <div
          {...listeners}
          {...attributes}
          className="flex-1 min-w-0 cursor-grab active:cursor-grabbing"
        >
          <div className="font-medium">
            {field.name || field.label || '-'}
          </div>
          {field.type && (
            <div className="text-xs text-muted-foreground mt-0.5">
              {field.type}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {mappedFieldCount > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onBadgeClick();
              }}
              className="h-5 min-w-[20px] px-1.5 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-semibold hover:bg-primary/90 transition-colors"
            >
              {mappedFieldCount > 9 ? '9+' : mappedFieldCount}
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewStats(nodeId, field);
            }}
            className="h-5 w-5 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            title="View Stats"
          >
            <BarChart3 className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewLineage(nodeId, field);
            }}
            className="h-5 w-5 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            title="View Lineage"
          >
            <GitBranch className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Sortable field item component
interface SortableFieldItemProps {
  field: NonNullable<PipelineNode['fields']>[0];
  isCalculated: boolean;
  totalMappedColumns: number;
  hasTransformer: boolean;
  onConfigClick: () => void;
  onDeleteClick: () => void;
  isHovered?: boolean;
  isRecentlyMapped?: boolean;
  currentNodeId?: string;
  getRelationship?: (relationshipId: string) => any;
  getNodeName?: (nodeId: string) => string | undefined;
  pendingRelationships?: Map<string, Partial<PipelineNodeRelationship>>;
  fieldRef?: (node: HTMLDivElement | null) => void;
  hasMissingConnectionParams?: boolean;
  /** When true, this field is managed by a template and locked — disable config/delete/drag */
  fieldTemplateLocked?: boolean;
}

function SortableFieldItem({
  field,
  isCalculated,
  totalMappedColumns,
  hasTransformer,
  onConfigClick,
  onDeleteClick,
  isHovered = false,
  isRecentlyMapped = false,
  currentNodeId,
  getRelationship,
  getNodeName,
  pendingRelationships,
  fieldRef,
  hasMissingConnectionParams = false,
  fieldTemplateLocked = false,
}: SortableFieldItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id || field.name || '' });

  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `drop-target-${field.id || field.name}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const combinedRef = (node: HTMLDivElement | null) => {
    setNodeRef(node);
    setDroppableRef(node);
    if (fieldRef) {
      fieldRef(node);
    }
  };

  const showHover = isOver || isHovered;
  const cannotMap = cannotAcceptMappings(field, isCalculated);
  const isPartOfCompositeKey = field.part_of_composite_key === true;

  return (
    <Card 
      ref={combinedRef} 
      style={style} 
      className={`p-3 transition-colors duration-500 ${
        isRecentlyMapped 
          ? 'bg-primary/20' 
          : showHover 
            ? cannotMap
              ? 'bg-destructive/10' 
              : 'bg-muted' 
            : ''
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          {...(fieldTemplateLocked ? {} : { ...attributes, ...listeners })}
          className={fieldTemplateLocked ? "flex-shrink-0" : "cursor-grab active:cursor-grabbing flex-shrink-0"}
        >
          <GripVertical className={`h-4 w-4 ${fieldTemplateLocked ? 'text-muted-foreground/30' : 'text-muted-foreground'}`} />
        </div>

        <div className="relative flex-shrink-0">
          {isCalculated ? (
            <Calculator className="h-5 w-5 text-primary" />
          ) : field.type === 'FOREIGN_KEY' ? (
            <Key className="h-5 w-5 text-primary" />
          ) : field.type === 'DENORMALIZED' ? (
            <Copy className="h-5 w-5 text-primary" />
          ) : (
            <>
              <Link2 className="h-5 w-5 text-muted-foreground" />
              {totalMappedColumns > 0 && (
                <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-semibold">
                  {totalMappedColumns > 9 ? '9+' : totalMappedColumns}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium truncate">
              {getFieldName(field, getRelationship, getNodeName, currentNodeId, pendingRelationships)}
            </span>
            {hasTransformer && (
              <Sparkles className="h-4 w-4 text-primary flex-shrink-0" />
            )}
          </div>
          {field.type && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {field.type}
            </p>
          )}
        </div>

        {isPartOfCompositeKey && (
          <span title="Part of merge key"><Merge className="h-4 w-4 text-secondary flex-shrink-0" /></span>
        )}

        {hasMissingConnectionParams && (
          <span title="Relationship is missing connection parameters">
            <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
          </span>
        )}

        <button
          onClick={onConfigClick}
          className={`h-5 w-5 flex items-center justify-center transition-colors flex-shrink-0 ${
            fieldTemplateLocked ? 'text-muted-foreground/30 cursor-not-allowed' : 'text-muted-foreground hover:text-foreground'
          }`}
          title={fieldTemplateLocked ? 'Locked by template' : 'Settings'}
          disabled={fieldTemplateLocked}
        >
          <Settings className="h-4 w-4" />
        </button>

        <button
          onClick={onDeleteClick}
          className={`h-5 w-5 flex items-center justify-center transition-colors flex-shrink-0 ${
            fieldTemplateLocked ? 'text-muted-foreground/30 cursor-not-allowed' : 'text-muted-foreground hover:text-destructive'
          }`}
          title={fieldTemplateLocked ? 'Locked by template' : 'Delete'}
          disabled={fieldTemplateLocked}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </Card>
  );
}

const CONNECTABLE_NODE_TYPES = ['SOURCE', 'STAGING', 'CUSTOM'];

export function getConnectableNodeOptions(
  allNodes: PipelineNode[],
  workingCopy: PipelineNode | null,
  currentNodeId: string | undefined,
) {
  if (!allNodes || !workingCopy || !currentNodeId) return [];

  const upstreamNodeIds = new Set(workingCopy.upstream_node_ids || []);

  return allNodes
    .filter((node) => {
      if (!CONNECTABLE_NODE_TYPES.includes(node.node_type)) {
        return false;
      }
      if (node.id === currentNodeId) {
        return false;
      }
      if (upstreamNodeIds.has(node.id)) {
        return false;
      }
      return true;
    })
    .map((node) => ({
      value: node.id,
      label: node.label || node.name || 'Unnamed Node',
      description: node.node_type,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

interface SourcesColumnsTabProps {
  workingCopy: PipelineNode | null;
  upstreamNodes: PipelineNode[];
  currentNodeId: string | undefined;
  allNodes: PipelineNode[];
  getRelationship?: (relationshipId: string) => any;
  getNodeName?: (nodeId: string) => string | undefined;
  pendingRelationships?: Map<string, Partial<PipelineNodeRelationship>>;
  onFieldsChange: (fields: NonNullable<PipelineNode['fields']>) => void;
  onFieldConfigOpen: (field: NonNullable<PipelineNode['fields']>[0], relationshipData?: Partial<PipelineNodeRelationship> | null) => void;
  onFieldDelete: (field: NonNullable<PipelineNode['fields']>[0]) => void;
  onAddField: (field: PipelineNodeField) => void;
  onUpstreamNodeAdd: (nodeId: string) => void;
  onGroupingBehaviorChange: (behavior: 'STACK' | 'MERGE' | 'DEDUPLICATE' | 'SUMMARIZE') => void;
  onUpstreamNodeRemove?: (nodeId: string) => void;
  onUpstreamPreFiltersChange?: (upstreamPreFilters: Record<string, DataWhitelist>) => void;
  onDedupeRulesChange?: (rules: DedupeRule[]) => void;
  /** When true, entity-level template locking: disables grouping and locks template-managed fields */
  templateLocked?: boolean;
}

export function SourcesColumnsTab({
  workingCopy,
  upstreamNodes,
  currentNodeId,
  allNodes,
  getRelationship,
  getNodeName,
  pendingRelationships,
  onFieldsChange,
  onFieldConfigOpen,
  onFieldDelete,
  onAddField,
  onUpstreamNodeAdd,
  onUpstreamNodeRemove,
  onGroupingBehaviorChange,
  onUpstreamPreFiltersChange,
  onDedupeRulesChange,
  templateLocked = false,
}: SourcesColumnsTabProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [fieldSearchQueries, setFieldSearchQueries] = useState<Record<string, string>>({});
  const [fieldPageNumbers, setFieldPageNumbers] = useState<Record<string, number>>({});
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [activeDragData, setActiveDragData] = useState<any>(null);
  const [hoveredDropTarget, setHoveredDropTarget] = useState<string | null>(null);
  const [recentlyMappedFieldId, setRecentlyMappedFieldId] = useState<string | null>(null);
  const [flashingFieldIds, setFlashingFieldIds] = useState<Set<string>>(new Set());
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [selectedNodeToConnect, setSelectedNodeToConnect] = useState<string>('');
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [selectedNodeForFilter, setSelectedNodeForFilter] = useState<string>('');
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [selectedNodeForStats, setSelectedNodeForStats] = useState<string>('');
  const [selectedFieldForStats, setSelectedFieldForStats] = useState<PipelineNodeField | null>(null);
  const [columnStats, setColumnStats] = useState<ColumnStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  
  // Use react-query for distribution with aggressive caching
  const columnDistributionQuery = useColumnValueDistribution(
    selectedNodeForStats,
    selectedFieldForStats?.id,
    20,
    statsDialogOpen && !!selectedNodeForStats && !!selectedFieldForStats?.id
  );
  const [lineageDialogOpen, setLineageDialogOpen] = useState(false);
  const [selectedNodeForLineage, setSelectedNodeForLineage] = useState<string>('');
  const [selectedFieldForLineage, setSelectedFieldForLineage] = useState<PipelineNodeField | null>(null);
  const [columnLineage, setColumnLineage] = useState<ColumnLineageGraphType | null>(null);
  const [loadingLineage, setLoadingLineage] = useState(false);
  const [disconnectNodeId, setDisconnectNodeId] = useState<string | null>(null);
  const [viewDataNodeId, setViewDataNodeId] = useState<string | null>(null);
  const [tiebreakerModalOpen, setTiebreakerModalOpen] = useState(false);
  const rightColumnScrollRef = useRef<HTMLDivElement>(null);
  const fieldRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Create a map of source_column_id -> all field IDs it's been mapped to
  const sourceColumnToFieldIdsMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    if (!workingCopy?.fields) return map;

    const addToMap = (sourceNodeId: string, attributeId: string | undefined, attributeKey: string | undefined, fieldId: string) => {
      if (!sourceNodeId) return;
      
      if (attributeId) {
        const sourceColumnId = `${sourceNodeId}:${attributeId}`;
        if (!map.has(sourceColumnId)) {
          map.set(sourceColumnId, new Set());
        }
        map.get(sourceColumnId)!.add(fieldId);
      }
      
      if (attributeKey && attributeKey !== attributeId) {
        const sourceColumnId = `${sourceNodeId}:${attributeKey}`;
        if (!map.has(sourceColumnId)) {
          map.set(sourceColumnId, new Set());
        }
        map.get(sourceColumnId)!.add(fieldId);
      }
    };

    workingCopy.fields.forEach((field) => {
      const fieldId = field.id || field.name || '';
      if (!field.map_options || field.map_options.length === 0) return;

      field.map_options.forEach((option) => {
        if (option.sub_options && option.sub_options.length > 0) {
          option.sub_options.forEach((sub) => {
            if (sub.source_node_id) {
              addToMap(sub.source_node_id, sub.attribute_id, sub.attribute_key, fieldId);
            }
          });
        } else {
          if (option.source_node_id) {
            addToMap(option.source_node_id, option.attribute_id, option.attribute_key, fieldId);
          }
        }
      });
    });

    return map;
  }, [workingCopy?.fields]);

  // Memoize available nodes for connection combobox
  const connectableNodeOptions = useMemo(() => {
    return getConnectableNodeOptions(allNodes, workingCopy, currentNodeId);
  }, [allNodes, workingCopy?.upstream_node_ids, currentNodeId]);

  // Validation for MERGE, SUMMARIZE, and DEDUPLICATE grouping behaviors
  const groupingValidation = useMemo(() => {
    const behavior = workingCopy?.grouping_behavior;
    if (!workingCopy || !['MERGE', 'SUMMARIZE', 'DEDUPLICATE'].includes(behavior || '')) {
      return { isValid: true, warnings: [] };
    }

    const warnings: string[] = [];

    // Check if there's at least one field marked as part_of_composite_key
    const compositeKeyFields = (workingCopy.fields || []).filter(
      (f) => f.part_of_composite_key === true
    );

    // Get the appropriate label for the error message based on grouping behavior
    const keyLabel = behavior === 'SUMMARIZE'
      ? 'group by'
      : behavior === 'DEDUPLICATE'
        ? 'dedupe key'
        : 'composite key';

    const modeLabel = behavior === 'SUMMARIZE'
      ? 'SUMMARIZE'
      : behavior === 'DEDUPLICATE'
        ? 'DEDUPLICATE'
        : 'MERGE';

    if (compositeKeyFields.length === 0) {
      warnings.push(`At least one column must be marked as part of ${keyLabel} for ${modeLabel} mode.`);
    } else if (behavior === 'MERGE') {
      // Additional check for MERGE: each upstream node must have at least one column mapped to a composite key field
      const upstreamNodeIds = new Set(workingCopy.upstream_node_ids || []);

      for (const upstreamNodeId of upstreamNodeIds) {
        const hasMappingToCompositeKey = compositeKeyFields.some((compositeField) => {
          const fieldMapOptions = compositeField.map_options || [];

          // Check if any map option comes from this upstream node
          return fieldMapOptions.some((option) => {
            // Handle sub_options if they exist
            if (option.sub_options && option.sub_options.length > 0) {
              return option.sub_options.some(
                (sub) => sub.source_node_id === upstreamNodeId
              );
            }
            return option.source_node_id === upstreamNodeId;
          });
        });

        if (!hasMappingToCompositeKey) {
          const upstreamNode = allNodes.find((n) => n.id === upstreamNodeId);
          const nodeName = upstreamNode?.label || upstreamNode?.name || 'Unknown Node';
          warnings.push(
            `No columns from "${nodeName}" are mapped to any composite key column.`
          );
        }
      }
    }

    return {
      isValid: warnings.length === 0,
      warnings,
    };
  }, [workingCopy, allNodes]);

  const toggleNodeExpansion = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const getFilteredFields = (node: PipelineNode) => {
    const fields = node.fields || [];
    const searchQuery = fieldSearchQueries[node.id]?.toLowerCase() || '';
    
    // First, filter out FOREIGN_KEY columns
    let filtered = fields.filter((field) => field.type !== 'FOREIGN_KEY');
    
    // Then apply search query if present
    if (searchQuery) {
      filtered = filtered.filter((field) => {
        const name = (field.name || '').toLowerCase();
        const label = (field.label || '').toLowerCase();
        return name.includes(searchQuery) || label.includes(searchQuery);
      });
    }
    
    const sorted = [...filtered].sort((a, b) => {
      const aName = (a.name || a.label || '').toLowerCase();
      const bName = (b.name || b.label || '').toLowerCase();
      return aName.localeCompare(bName);
    });
    
    return {
      fields: sorted,
      totalMatching: filtered.length,
    };
  };

  const scrollFieldIntoView = useCallback((fieldId: string) => {
    const fieldElement = fieldRefsMap.current.get(fieldId);
    const scrollContainer = rightColumnScrollRef.current;
    
    if (!fieldElement || !scrollContainer) return;
    
    const containerRect = scrollContainer.getBoundingClientRect();
    const elementRect = fieldElement.getBoundingClientRect();
    
    const isVisible = 
      elementRect.bottom > containerRect.top &&
      elementRect.top < containerRect.bottom;
    
    if (!isVisible) {
      fieldElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    }
  }, []);

  const handleViewStats = useCallback(async (nodeId: string, field: PipelineNodeField) => {
    setSelectedNodeForStats(nodeId);
    setSelectedFieldForStats(field);
    setStatsDialogOpen(true);
    setLoadingStats(true);
    setColumnStats(null);

    const fieldId = field.id;
    const fieldName = field.name;
    const fieldKey = fieldId || fieldName;
    
    if (!fieldKey) {
      toastService.error('Field ID or name is required');
      setLoadingStats(false);
      return;
    }

    try {
      // First, try to get stats from the node's shape data
      const node = upstreamNodes.find(n => n.id === nodeId);
      
      if (node?.shape?.columns) {
        // Try to find shape column by matching either field.id or field.name
        const shapeColumn = node.shape.columns.find((c: any) => 
          c.key === fieldId || c.key === fieldName
        );
        
        if (shapeColumn && shapeColumn.enhanced) {
          // We have enhanced shape data, use it
          setColumnStats({
            key: shapeColumn.key,
            distinct_values: shapeColumn.distinct_values || 0,
            empty_values: shapeColumn.empty_values || 0,
            uniqueness: shapeColumn.uniqueness || 0,
            density: shapeColumn.density || 0,
            samples: shapeColumn.samples || [],
            enhanced: true,
          });
          setLoadingStats(false);
        } else {
          // If shape data is not available or not enhanced, fetch from API
          const result = await ApiService.getInstance().request(
            'GET',
            `/pipelinenodes/${nodeId}/column/${fieldKey}/shape`
          ) as SingleRecordResponse<ColumnStats>;
          
          setColumnStats(result.record);
          setLoadingStats(false);
        }
      } else {
        // If shape data is not available, fetch from API
        const result = await ApiService.getInstance().request(
          'GET',
          `/pipelinenodes/${nodeId}/column/${fieldKey}/shape`
        ) as SingleRecordResponse<ColumnStats>;
        
        setColumnStats(result.record);
        setLoadingStats(false);
      }

      // Distribution is now handled by react-query hook (useColumnValueDistribution)
      // No need to manually fetch it here
    } catch (err) {
      toastService.error('Failed to load column statistics');
      console.error('Error loading column stats:', err);
    } finally {
      setLoadingStats(false);
    }
  }, [upstreamNodes]);

  const handleViewLineage = useCallback(async (nodeId: string, field: PipelineNodeField) => {
    setSelectedNodeForLineage(nodeId);
    setSelectedFieldForLineage(field);
    setLineageDialogOpen(true);
    setLoadingLineage(true);
    setColumnLineage(null);

    const fieldId = field.id;
    
    if (!fieldId) {
      toastService.error('Field ID is required');
      setLoadingLineage(false);
      return;
    }

    try {
      const result = await ApiService.getInstance().request(
        'GET',
        `/pipelinenodes/${nodeId}/column-lineage`,
        { field_id: fieldId }
      ) as ColumnLineageGraphType;
      
      setColumnLineage(result);
    } catch (err) {
      toastService.error('Failed to load column lineage');
      console.error('Error loading column lineage:', err);
    } finally {
      setLoadingLineage(false);
    }
  }, []);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragId(null);
    setActiveDragData(null);
    setHoveredDropTarget(null);
    
    if (active.data.current?.type === 'source-field' && over) {
      const overId = typeof over.id === 'string' ? over.id : String(over.id);
      
      if (overId.startsWith('drop-target-')) {
        const targetFieldId = overId.replace('drop-target-', '');
        const sourceData = active.data.current;
        
        if (!workingCopy || !sourceData) return;
        
        const targetField = workingCopy.fields?.find(
          (f) => (f.id || f.name || '') === targetFieldId
        );
        
        if (!targetField) return;
        
        const isCalculated = (targetField as any).advanced_mode === true;
        if (cannotAcceptMappings(targetField, isCalculated)) {
          toastService.error('Source columns cannot be mapped to this column');
          return;
        }
        
        const updatedFields = workingCopy.fields?.map((f) => {
          const fieldId = f.id || f.name || '';
          if (fieldId === targetFieldId) {
            const existingMapping = f.map_options?.some(
              (mo) => mo.source_node_id === sourceData.nodeId && 
                      (mo.attribute_id === sourceData.attributeId || mo.attribute_key === sourceData.attributeKey)
            );
            
            if (existingMapping) {
              return f;
            }
            
            const newMapOption: PipelineNodeMapOption = {
              id: `${sourceData.nodeId}:${sourceData.fieldId}`,
              source_node_id: sourceData.nodeId,
              attribute_key: sourceData.attributeKey,
              attribute_id: sourceData.attributeId,
            };
            
            return {
              ...f,
              map_options: [...(f.map_options || []), newMapOption],
            };
          }
          return f;
        });
        
        if (updatedFields) {
          onFieldsChange(updatedFields);
          setRecentlyMappedFieldId(targetFieldId);
          toastService.success('Column mapped');
          setTimeout(() => {
            setRecentlyMappedFieldId(null);
          }, 500);
        }
        return;
      }
      
      const targetField = workingCopy?.fields?.find(
        (f) => (f.id || f.name || '') === overId
      );
      
      if (targetField && active.data.current?.type === 'source-field') {
        const sourceData = active.data.current;
        
        if (!workingCopy || !sourceData) return;
        
        const isCalculated = (targetField as any).advanced_mode === true;
        if (cannotAcceptMappings(targetField, isCalculated)) {
          toastService.error('Source columns cannot be mapped to this column');
          return;
        }
        
        const updatedFields = workingCopy.fields?.map((f) => {
          const fieldId = f.id || f.name || '';
          if (fieldId === overId) {
            const existingMapping = f.map_options?.some(
              (mo) => mo.source_node_id === sourceData.nodeId && 
                      (mo.attribute_id === sourceData.attributeId || mo.attribute_key === sourceData.attributeKey)
            );
            
            if (existingMapping) {
              return f;
            }
            
            const newMapOption: PipelineNodeMapOption = {
              id: `${sourceData.nodeId}:${sourceData.fieldId}`,
              source_node_id: sourceData.nodeId,
              attribute_key: sourceData.attributeKey,
              attribute_id: sourceData.attributeId,
            };
            
            return {
              ...f,
              map_options: [...(f.map_options || []), newMapOption],
            };
          }
          return f;
        });
        
        if (updatedFields) {
          onFieldsChange(updatedFields);
          setRecentlyMappedFieldId(overId);
          toastService.success('Column mapped');
          setTimeout(() => {
            setRecentlyMappedFieldId(null);
          }, 500);
        }
        return;
      }
    }
    
    if (!over || active.id === over.id || !workingCopy || !workingCopy.fields) return;
    
    if (active.data.current?.type !== 'source-field') {
      const oldIndex = workingCopy.fields.findIndex(
        (f) => (f.id || f.name) === active.id
      );
      const newIndex = workingCopy.fields.findIndex(
        (f) => (f.id || f.name) === over.id
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        const newFields = arrayMove(workingCopy.fields, oldIndex, newIndex);
        onFieldsChange(newFields);
      }
    }
  };

  return (
    <div className="flex-1 overflow-hidden px-4 py-4 mt-4 flex flex-col h-full">
      <DndContext
        sensors={sensors}
        collisionDetection={customCollisionDetection}
        onDragStart={(event) => {
          setActiveDragId(event.active.id as string);
          setActiveDragData(event.active.data.current);
          setHoveredDropTarget(null);
        }}
        onDragEnd={handleDragEnd}
      >
        <DragMonitor setHoveredDropTarget={setHoveredDropTarget} workingCopy={workingCopy} />

        {/* Record Handling Header Section */}
        {upstreamNodes.length >= 1 && (
          <Card className="mb-4">
            <CardContent className="py-3 px-4">
              <div className="flex items-start gap-6">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Record Handling</Label>
                  <Select
                    value={workingCopy?.grouping_behavior || 'STACK'}
                    onValueChange={(value) => {
                      onGroupingBehaviorChange(value as 'STACK' | 'MERGE' | 'DEDUPLICATE' | 'SUMMARIZE');
                    }}
                    disabled={templateLocked}
                  >
                    <SelectTrigger className="h-9 w-[160px]">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {upstreamNodes.length === 1 ? (
                        <>
                          <SelectItem value="STACK">No Grouping</SelectItem>
                          <SelectItem value="DEDUPLICATE">Deduplicate</SelectItem>
                          <SelectItem value="SUMMARIZE">Summarize</SelectItem>
                        </>
                      ) : (
                        <>
                          <SelectItem value="STACK">Stack</SelectItem>
                          <SelectItem value="MERGE">Merge</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                {/* Key Column Selection for MERGE/SUMMARIZE/DEDUPLICATE */}
                {['MERGE', 'SUMMARIZE', 'DEDUPLICATE'].includes(workingCopy?.grouping_behavior || '') && (
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {workingCopy?.grouping_behavior === 'SUMMARIZE'
                        ? 'Group by'
                        : workingCopy?.grouping_behavior === 'DEDUPLICATE'
                          ? 'Dedupe key'
                          : 'Merge key'}
                    </Label>
                    <div className="flex items-center gap-2">
                      <MultiSelectCombobox
                      options={(workingCopy?.fields || [])
                        .filter(field =>
                          field.type !== 'FOREIGN_KEY' &&
                          field.type !== 'DENORMALIZED' &&
                          !(field as any).advanced_mode
                        )
                        .map(field => ({
                          value: field.id || field.name || '',
                          label: field.label || field.name || 'Unnamed Column',
                        }))}
                      value={(workingCopy?.fields || [])
                        .filter(field => field.part_of_composite_key === true)
                        .map(field => field.id || field.name || '')}
                      onValueChange={(selectedIds) => {
                        const updatedFields = (workingCopy?.fields || []).map(field => ({
                          ...field,
                          part_of_composite_key: selectedIds.includes(field.id || field.name || ''),
                        }));
                        onFieldsChange(updatedFields);
                      }}
                      placeholder={
                        workingCopy?.grouping_behavior === 'SUMMARIZE'
                          ? 'Select columns to group by...'
                          : workingCopy?.grouping_behavior === 'DEDUPLICATE'
                            ? 'Select dedupe key columns...'
                            : 'Select merge key columns...'
                      }
                      emptyText="No columns available."
                      className="flex-1"
                    />
                    {workingCopy?.grouping_behavior === 'DEDUPLICATE' && (
                      <Button
                        variant="outline"
                        className="h-9"
                        onClick={() => setTiebreakerModalOpen(true)}
                      >
                        <Settings className="h-4 w-4 mr-1" />
                        Tiebreaker Rules
                      </Button>
                    )}
                    </div>
                </div>
              )}
              </div>
              {!groupingValidation.isValid && (
                <div className="mt-3 space-y-1">
                  {groupingValidation.warnings.map((warning, idx) => (
                    <div
                      key={idx}
                      className="text-xs text-destructive font-medium bg-destructive/10 border border-destructive/80 rounded p-2"
                    >
                      {warning}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-2 gap-6 flex-1 min-h-0">
          {/* Left Column - Source Nodes */}
          <div className="space-y-4 overflow-y-auto pr-2 min-h-0">
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold">Source Nodes</h3>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setConnectDialogOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Connect
                </Button>
              </div>
              {upstreamNodes.length === 0 ? (
                <p className="text-sm text-muted-foreground">No upstream nodes connected</p>
              ) : (
                <div className="space-y-3">
                  {upstreamNodes.map((upstreamNode: PipelineNode) => {
                    const isExpanded = expandedNodes.has(upstreamNode.id);
                    const { fields: allFilteredFields, totalMatching } = getFilteredFields(upstreamNode);
                    const searchQuery = fieldSearchQueries[upstreamNode.id] || '';
                    
                    const itemsPerPage = 8;
                    const currentPage = fieldPageNumbers[upstreamNode.id] || 1;
                    const totalPages = Math.ceil(totalMatching / itemsPerPage);
                    const startIndex = (currentPage - 1) * itemsPerPage;
                    const endIndex = startIndex + itemsPerPage;
                    const fields = allFilteredFields.slice(startIndex, endIndex);
                    
                    return (
                      <Card key={upstreamNode.id}>
                        <CardHeader className="px-3 pt-3 pb-3">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <CardTitle className="text-sm font-medium truncate">
                                {upstreamNode.label || upstreamNode.name || 'Unnamed Node'}
                              </CardTitle>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={() => {
                                  setSelectedNodeForFilter(upstreamNode.id);
                                  setFilterDialogOpen(true);
                                }}
                                className="h-5 w-5 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                                title="Filter upstream data"
                              >
                                <Filter className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => setDisconnectNodeId(upstreamNode.id)}
                                className="h-5 w-5 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                                title="Disconnect source"
                              >
                                <Unplug className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => toggleNodeExpansion(upstreamNode.id)}
                                className="h-5 w-5 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                              >
                                {isExpanded ? (
                                  <ChevronUp className="h-3.5 w-3.5" />
                                ) : (
                                  <ChevronDown className="h-3.5 w-3.5" />
                                )}
                              </button>
                            </div>
                          </div>
                        </CardHeader>
                        {isExpanded && (
                          <CardContent className="px-3 pt-0">
                            <div className="space-y-3">
                              <div className="flex items-center gap-2">
                                <div className="relative flex-1">
                                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                                  <Input
                                    placeholder="Search columns..."
                                    value={searchQuery}
                                    onChange={(e) => {
                                      const newQuery = e.target.value;
                                      setFieldSearchQueries((prev) => ({
                                        ...prev,
                                        [upstreamNode.id]: newQuery,
                                      }));
                                      if (newQuery !== searchQuery) {
                                        setFieldPageNumbers((prev) => ({
                                          ...prev,
                                          [upstreamNode.id]: 1,
                                        }));
                                      }
                                    }}
                                    className="pl-8"
                                  />
                                </div>
                                <button
                                  onClick={() => setViewDataNodeId(upstreamNode.id)}
                                  className="h-8 w-8 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                                  title="View data"
                                >
                                  <Table2 className="h-4 w-4" />
                                </button>
                              </div>
                              
                              {allFilteredFields.length > 0 ? (
                                <>
                                  <div className="space-y-1">
                                    {fields.map((field) => {
                                      const attributeId = field.id || field.name;
                                      const attributeKey = field.name || field.label;
                                      const sourceColumnId1 = `${upstreamNode.id}:${attributeId}`;
                                      const sourceColumnId2 = `${upstreamNode.id}:${attributeKey}`;
                                      
                                      const mappedFieldIds1 = sourceColumnToFieldIdsMap.get(sourceColumnId1);
                                      const mappedFieldIds2 = sourceColumnToFieldIdsMap.get(sourceColumnId2);
                                      
                                      const allMappedFieldIds = new Set<string>();
                                      if (mappedFieldIds1) {
                                        mappedFieldIds1.forEach(id => allMappedFieldIds.add(id));
                                      }
                                      if (mappedFieldIds2) {
                                        mappedFieldIds2.forEach(id => allMappedFieldIds.add(id));
                                      }
                                      
                                      const mappedCount = allMappedFieldIds.size;
                                      
                                      return (
                                        <DraggableSourceField
                                          key={field.id || field.name}
                                          nodeId={upstreamNode.id}
                                          field={field}
                                          mappedFieldCount={mappedCount}
                                          onBadgeClick={() => {
                                            if (allMappedFieldIds.size > 0) {
                                              const fieldIdsArray = Array.from(allMappedFieldIds);
                                              
                                              if (fieldIdsArray.length > 0) {
                                                scrollFieldIntoView(fieldIdsArray[0]);
                                              }
                                              
                                              setTimeout(() => {
                                                setFlashingFieldIds(new Set(allMappedFieldIds));
                                                setTimeout(() => {
                                                  setFlashingFieldIds(new Set());
                                                }, 500);
                                              }, 100);
                                            }
                                          }}
                                          onViewStats={handleViewStats}
                                          onViewLineage={handleViewLineage}
                                        />
                                      );
                                    })}
                                  </div>
                                  
                                  {totalMatching > itemsPerPage && (
                                    <div className="flex items-center justify-center gap-2 pt-2 border-t">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={() => {
                                          setFieldPageNumbers((prev) => ({
                                            ...prev,
                                            [upstreamNode.id]: Math.max(1, (prev[upstreamNode.id] || 1) - 1),
                                          }));
                                        }}
                                        disabled={currentPage === 1}
                                      >
                                        <ChevronLeft className="h-4 w-4" />
                                      </Button>
                                      <span className="text-xs text-muted-foreground min-w-[60px] text-center">
                                        {startIndex + 1}-{Math.min(endIndex, totalMatching)} of {totalMatching}
                                      </span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={() => {
                                          setFieldPageNumbers((prev) => ({
                                            ...prev,
                                            [upstreamNode.id]: Math.min(totalPages, (prev[upstreamNode.id] || 1) + 1),
                                          }));
                                        }}
                                        disabled={currentPage >= totalPages}
                                      >
                                        <ChevronRight className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <p className="text-sm text-muted-foreground">
                                  {searchQuery ? 'No columns match your search' : 'No columns available'}
                                </p>
                              )}
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          
          {/* Right Column - Current Node Columns */}
          <div ref={rightColumnScrollRef} className="space-y-4 overflow-y-auto pl-2 min-h-0">
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold">Columns</h3>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8">
                      <Plus className="h-4 w-4 mr-1" />
                      Add New
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    <DropdownMenuItem
                      onClick={() => {
                        if (!workingCopy) return;

                        const fieldId = `new-col-${Date.now()}`;
                        const newField: PipelineNodeField = {
                          id: fieldId,
                          name: `new_column_${Date.now()}`,
                          label: `new_column_${Date.now()}`,
                          type: 'STRING',
                          description: '',
                          map_options: [],
                        };

                        onAddField(newField);
                        onFieldConfigOpen(newField, null);
                      }}
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">Mappable Column</span>
                        <span className="text-xs text-muted-foreground">
                          Mappable columns let you connect columns from source nodes
                        </span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        if (!workingCopy) return;

                        const fieldId = `new-calc-${Date.now()}`;
                        const newField: PipelineNodeField = {
                          id: fieldId,
                          name: `calculated_column_${Date.now()}`,
                          label: `calculated_column_${Date.now()}`,
                          type: 'STRING',
                          description: '',
                          custom_sql: '',
                          map_options: [],
                        };

                        const newFieldWithAdvanced = {
                          ...newField,
                          advanced_mode: true,
                        } as any;

                        onAddField(newFieldWithAdvanced);
                        onFieldConfigOpen(newFieldWithAdvanced, null);
                      }}
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">Calculated Column</span>
                        <span className="text-xs text-muted-foreground">
                          Calculated columns let you write a custom SQL formula using the values in other columns
                        </span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        if (!workingCopy) return;

                        const fieldId = `new-fk-${Date.now()}`;
                        const newField: PipelineNodeField = {
                          id: fieldId,
                          name: `foreign_key_${Date.now()}`,
                          label: `foreign_key_${Date.now()}`,
                          type: 'FOREIGN_KEY',
                          description: '',
                          map_options: [],
                        };

                        onAddField(newField);
                        onFieldConfigOpen(newField, {
                          child_node_id: currentNodeId || '',
                          parent_node_id: '',
                          name: '',
                          description: '',
                          child_foreign_key_name: newField.name || '',
                          parent_lookup_logic_gate: 'AND',
                          parent_lookups: [],
                        });
                      }}
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">Foreign Key</span>
                        <span className="text-xs text-muted-foreground">
                          Foreign key columns let you connect this node to another node
                        </span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        if (!workingCopy) return;

                        const fieldId = `new-denorm-${Date.now()}`;
                        const newField: PipelineNodeField = {
                          id: fieldId,
                          name: `denormalized_column_${Date.now()}`,
                          label: `denormalized_column_${Date.now()}`,
                          type: 'DENORMALIZED',
                          description: '',
                          map_options: [],
                        };

                        onAddField(newField);
                        onFieldConfigOpen(newField, null);
                      }}
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">Denormalized Column</span>
                        <span className="text-xs text-muted-foreground">
                          Denormalized columns let you bring data in from a connected node (via a foreign key) onto this node
                        </span>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              {!workingCopy?.fields || workingCopy.fields.length === 0 ? (
                <p className="text-sm text-muted-foreground">No columns defined</p>
              ) : (
                <SortableContext
                  items={workingCopy.fields.map((f) => f.id || f.name || '')}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {workingCopy.fields.map((field) => {
                      const isCalculated = (field as any).advanced_mode === true;
                      const totalMappedColumns = countMappedColumns(field);
                      const hasTransformer = !!(field.transformer);
                      const fieldId = field.id || field.name || '';
                      const dropTargetId = `drop-target-${fieldId}`;
                      const isHovered = hoveredDropTarget === dropTargetId;
                      const isRecentlyMapped = recentlyMappedFieldId === fieldId || flashingFieldIds.has(fieldId);

                      // Check if FOREIGN_KEY field is missing connection parameters
                      let hasMissingConnectionParams = false;
                      if (field.type === 'FOREIGN_KEY') {
                        if ((field as PipelineNodeField).relationship_id && getRelationship) {
                          // Check existing relationship
                          const relationship = getRelationship((field as PipelineNodeField).relationship_id!);
                          hasMissingConnectionParams = !relationship?.parent_lookups || relationship.parent_lookups.length === 0;
                        } else if (pendingRelationships?.has(fieldId)) {
                          // Check pending relationship
                          const pendingRel = pendingRelationships.get(fieldId);
                          hasMissingConnectionParams = !pendingRel?.parent_lookups || pendingRel.parent_lookups.length === 0;
                        } else {
                          // No relationship data at all - definitely missing
                          hasMissingConnectionParams = true;
                        }
                      }

                      // Field is template-locked if the tab-level templateLocked is true AND the field itself is managed by the template
                      const isFieldTemplateLocked = templateLocked && !!(field as PipelineNodeField).managed_by_template_id;

                      return (
                        <SortableFieldItem
                          key={field.id || field.name}
                          field={field}
                          isCalculated={isCalculated}
                          totalMappedColumns={totalMappedColumns}
                          hasTransformer={hasTransformer}
                          isHovered={isHovered}
                          isRecentlyMapped={isRecentlyMapped}
                          currentNodeId={currentNodeId}
                          getRelationship={getRelationship}
                          getNodeName={getNodeName}
                          pendingRelationships={pendingRelationships}
                          hasMissingConnectionParams={hasMissingConnectionParams}
                          fieldTemplateLocked={isFieldTemplateLocked}
                          fieldRef={(node) => {
                            if (node) {
                              fieldRefsMap.current.set(fieldId, node);
                            } else {
                              fieldRefsMap.current.delete(fieldId);
                            }
                          }}
                          onConfigClick={() => {
                            if (field.type === 'FOREIGN_KEY') {
                              const fieldKey = field.id || field.name || '';
                              // Check for existing relationship first
                              if ((field as PipelineNodeField).relationship_id && getRelationship) {
                                const relationship = getRelationship((field as PipelineNodeField).relationship_id!);
                                onFieldConfigOpen(field, relationship || {
                                  child_node_id: currentNodeId || '',
                                  parent_node_id: '',
                                  name: '',
                                  description: '',
                                  child_foreign_key_name: field.name || '',
                                  parent_lookup_logic_gate: 'AND',
                                  parent_lookups: [],
                                });
                              } else if (pendingRelationships?.has(fieldKey)) {
                                // Check for pending relationship (not yet saved to server)
                                onFieldConfigOpen(field, pendingRelationships.get(fieldKey)!);
                              } else {
                                // New FOREIGN_KEY with no relationship data yet
                                onFieldConfigOpen(field, {
                                  child_node_id: currentNodeId || '',
                                  parent_node_id: '',
                                  name: '',
                                  description: '',
                                  child_foreign_key_name: field.name || '',
                                  parent_lookup_logic_gate: 'AND',
                                  parent_lookups: [],
                                });
                              }
                            } else {
                              onFieldConfigOpen(field, null);
                            }
                          }}
                          onDeleteClick={() => {
                            onFieldDelete(field);
                          }}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              )}
            </div>
          </div>
        </div>
        <DragOverlay>
          {activeDragId && activeDragData?.type === 'source-field' ? (
            <div className="px-3 py-2 bg-muted rounded-md text-sm shadow-lg border border-border">
              <div className="font-medium">
                {activeDragData.fieldName || '-'}
              </div>
              {activeDragData.fieldType && (
                <div className="text-xs text-muted-foreground mt-0.5">
                  {activeDragData.fieldType}
                </div>
              )}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      
      {/* Connect Node Dialog */}
      <Dialog open={connectDialogOpen} onOpenChange={setConnectDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Connect Source Node</DialogTitle>
            <DialogDescription>
              Select a source, staging, or custom node to connect as an upstream dependency
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4 py-4">
            <Combobox
              options={connectableNodeOptions}
              value={selectedNodeToConnect}
              onValueChange={setSelectedNodeToConnect}
              placeholder="Select a node to connect..."
            />
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConnectDialogOpen(false);
                setSelectedNodeToConnect('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedNodeToConnect) {
                  onUpstreamNodeAdd(selectedNodeToConnect);
                  setConnectDialogOpen(false);
                  setSelectedNodeToConnect('');
                  toastService.success('Node connected');
                }
              }}
              disabled={!selectedNodeToConnect}
            >
              Connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Filter Upstream Data Dialog */}
      <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
        <DialogContent className="sm:max-w-[800px]">
          <DialogHeader>
            <DialogTitle>Filter Upstream Data</DialogTitle>
            <DialogDescription>
              {selectedNodeForFilter && (() => {
                const node = upstreamNodes.find(n => n.id === selectedNodeForFilter);
                const nodeName = node?.label || node?.name || 'Unknown Node';
                return `Filter out records in the connected node "${nodeName}" prior to it being mapped to this node.`;
              })()}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4 py-4">
            {selectedNodeForFilter && (() => {
              const selectedNode = upstreamNodes.find(n => n.id === selectedNodeForFilter);
              if (!selectedNode) return null;
              
              // Get current filters for this upstream node
              const currentFilters: DataWhitelist = workingCopy?.upstream_pre_filters?.[selectedNodeForFilter] || {
                entries: [],
                logic_gate: 'AND',
              };
              
              // Create column options from the upstream node's fields (excluding FOREIGN_KEY)
              const columnOptions: ComboboxOption[] = (selectedNode.fields || [])
                .filter(field => field.type !== 'FOREIGN_KEY')
                .map(field => ({
                  value: `${selectedNode.id}:${field.id || field.name}`,
                  label: field.label || field.name || 'Unnamed Column',
                  description: field.description,
                }));
              
              // Convert DataWhitelist entries to FilterEntry format
              const filterEntries: FilterEntry[] = currentFilters.entries.map(entry => ({
                columnId: `${entry.pipeline_node_id}:${entry.field_id}`,
                comparator: entry.comparator,
                value: entry.value,
              }));

              return (
                <FilterTable
                  filters={filterEntries}
                  onChange={(newFilters) => {
                    if (!workingCopy || !onUpstreamPreFiltersChange) return;

                    // Convert FilterEntry back to DataWhitelistEntry
                    const newEntries: DataWhitelistEntry[] = newFilters.map(f => {
                      const [pipelineNodeId, fieldId] = f.columnId.split(':');
                      return {
                        pipeline_node_id: pipelineNodeId || '',
                        field_id: fieldId || '',
                        comparator: f.comparator,
                        value: f.value,
                      };
                    });

                    const newWhitelist: DataWhitelist = {
                      ...currentFilters,
                      entries: newEntries,
                    };

                    const updatedFilters = {
                      ...(workingCopy.upstream_pre_filters || {}),
                      [selectedNodeForFilter]: newWhitelist,
                    };

                    onUpstreamPreFiltersChange(updatedFilters);
                  }}
                  columnOptions={columnOptions}
                  columnLabel="Column"
                  showComparator={true}
                  valueMode="text"
                />
              );
            })()}
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setFilterDialogOpen(false);
                setSelectedNodeForFilter('');
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Column Stats Dialog */}
      <Dialog open={statsDialogOpen} onOpenChange={setStatsDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Column Statistics</DialogTitle>
            <DialogDescription>
              {selectedFieldForStats && (() => {
                const node = upstreamNodes.find(n => n.id === selectedNodeForStats);
                const nodeName = node?.label || node?.name || 'Unknown Node';
                const fieldName = selectedFieldForStats.label || selectedFieldForStats.name || 'Unknown Column';
                return `Statistics for "${fieldName}" from "${nodeName}"`;
              })()}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4 py-4">
            {loadingStats ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-muted-foreground">Loading statistics...</div>
              </div>
            ) : columnStats ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Density</div>
                    <div className="text-2xl font-semibold">
                      {formatPercentage(columnStats.density)}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Density measures how many records have a value in this column. A density of 100% means every record has a value, while lower percentages indicate missing or null values.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Uniqueness</div>
                    <div className="text-2xl font-semibold">
                      {formatPercentage(columnStats.uniqueness)}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Uniqueness measures how many distinct values exist in this column. A uniqueness of 100% means every value is unique, while lower percentages indicate duplicate values.
                    </p>
                  </div>
                </div>
                
                {columnStats.distinct_values !== undefined && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Distinct Values</div>
                    <div className="text-lg">
                      {columnStats.distinct_values.toLocaleString()}
                    </div>
                  </div>
                )}
                
                {columnStats.empty_values !== undefined && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Empty Values</div>
                    <div className="text-lg">
                      {columnStats.empty_values.toLocaleString()}
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  <div className="text-sm font-medium">Most Common Values</div>
                  {columnDistributionQuery.isLoading ? (
                    <div className="text-sm text-muted-foreground py-2">Loading distribution...</div>
                  ) : columnDistributionQuery.data && columnDistributionQuery.data.length > 0 ? (
                    <div className="space-y-2">
                      {columnDistributionQuery.data.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-2 bg-muted rounded"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-mono truncate" title={String(item.value)}>
                              {item.value === null || item.value === '' ? (
                                <span className="text-muted-foreground italic">(empty)</span>
                              ) : (
                                String(item.value)
                              )}
                            </div>
                          </div>
                          <div className="ml-4 text-sm font-semibold">
                            {item.total.toLocaleString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground py-2">
                      No distribution data available
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground py-4">
                No statistics available for this column.
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setStatsDialogOpen(false);
                setSelectedNodeForStats('');
                setSelectedFieldForStats(null);
                setColumnStats(null);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Column Lineage Dialog */}
      <FullScreenTakeover
        show={lineageDialogOpen}
        onHide={() => {
          setLineageDialogOpen(false);
          setSelectedNodeForLineage('');
          setSelectedFieldForLineage(null);
          setColumnLineage(null);
        }}
        noCloseButton={true}
        title={
          selectedFieldForLineage
            ? (() => {
                const node = upstreamNodes.find(n => n.id === selectedNodeForLineage);
                const nodeName = node?.label || node?.name || 'Unknown Node';
                const fieldName = selectedFieldForLineage.label || selectedFieldForLineage.name || 'Unknown Column';
                return `Column Lineage: "${fieldName}" from "${nodeName}"`;
              })()
            : 'Column Lineage'
        }
      >
        <div className="h-full">
          {loadingLineage ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-sm text-muted-foreground">Loading lineage...</div>
            </div>
          ) : columnLineage ? (
            <div className="h-full w-full">
              <ColumnLineageGraph 
                graph={columnLineage}
                selectedNodeId={selectedNodeForLineage}
                selectedFieldId={selectedFieldForLineage?.id}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-sm text-muted-foreground">No lineage data available</div>
            </div>
          )}
        </div>
      </FullScreenTakeover>

      {/* View Node Data */}
      <FullScreenTakeover
        show={!!viewDataNodeId}
        onHide={() => setViewDataNodeId(null)}
        title={(() => {
          if (!viewDataNodeId) return 'Node Data';
          const node = upstreamNodes.find(n => n.id === viewDataNodeId);
          return `Data: ${node?.label || node?.name || 'Node'}`;
        })()}
      >
        {viewDataNodeId && (
          <div className="p-6 h-full">
            <NodeDataTable pipelineNodeId={viewDataNodeId} />
          </div>
        )}
      </FullScreenTakeover>

      {/* Disconnect Source Confirmation */}
      <AlertDialog open={!!disconnectNodeId} onOpenChange={(open) => { if (!open) setDisconnectNodeId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Source</AlertDialogTitle>
            <AlertDialogDescription>
              {disconnectNodeId && (() => {
                const node = upstreamNodes.find(n => n.id === disconnectNodeId);
                const nodeName = node?.label || node?.name || 'Unknown Node';
                return <>This will disconnect <strong>{nodeName}</strong> from this node and unmap all of its columns.</>;
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (disconnectNodeId && onUpstreamNodeRemove) {
                  onUpstreamNodeRemove(disconnectNodeId);
                }
                setDisconnectNodeId(null);
              }}
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tiebreaker Rules Modal for DEDUPLICATE mode */}
      <Dialog open={tiebreakerModalOpen} onOpenChange={setTiebreakerModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Tiebreaker Rules</DialogTitle>
            <DialogDescription>
              When duplicate records are found, these rules determine which record to keep. Rules are applied in order.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
          {(() => {
            const dedupeRules = workingCopy?.dedupe_rules || [];

            // Build rule options from fields
            const ruleOptions: ComboboxOption[] = [
              { value: '_PLB_LOADED_AT:DESC', label: 'Keep most recent (by load time)' },
              { value: '_PLB_LOADED_AT:ASC', label: 'Keep first (by load time)' },
              ...(workingCopy?.fields || [])
                .filter(field => field.type !== 'FOREIGN_KEY' && field.type !== 'DENORMALIZED')
                .flatMap(field => [
                  {
                    value: `${field.id || field.name}:DESC`,
                    label: `Newest ${field.label || field.name}`,
                  },
                  {
                    value: `${field.id || field.name}:ASC`,
                    label: `Oldest ${field.label || field.name}`,
                  },
                  {
                    value: `${field.id || field.name}:NON_EMPTY`,
                    label: `First non-empty ${field.label || field.name}`,
                  },
                ]),
            ];

            const parseRuleOption = (value: string): DedupeRule => {
              const parts = value.split(':');
              if (parts.length === 2) {
                if (parts[1] === 'NON_EMPTY') {
                  return { field_id: parts[0], comparator: 'NOT_EMPTY' };
                }
                return { field_id: parts[0], desc: parts[1] === 'DESC' };
              }
              return { field_id: parts[0], desc: false };
            };

            const ruleToOptionValue = (rule: DedupeRule): string => {
              if (rule.comparator === 'NOT_EMPTY') {
                return `${rule.field_id}:NON_EMPTY`;
              }
              return `${rule.field_id}:${rule.desc ? 'DESC' : 'ASC'}`;
            };

            const handleAddRule = () => {
              if (onDedupeRulesChange) {
                onDedupeRulesChange([...dedupeRules, { field_id: '_PLB_LOADED_AT', desc: true }]);
              }
            };

            const handleRemoveRule = (index: number) => {
              if (onDedupeRulesChange) {
                onDedupeRulesChange(dedupeRules.filter((_, i) => i !== index));
              }
            };

            const handleUpdateRule = (index: number, optionValue: string) => {
              if (onDedupeRulesChange) {
                const newRule = parseRuleOption(optionValue);
                const newRules = [...dedupeRules];
                newRules[index] = newRule;
                onDedupeRulesChange(newRules);
              }
            };

            const handleReorderRules = (oldIndex: number, newIndex: number) => {
              if (onDedupeRulesChange) {
                const newRules = [...dedupeRules];
                const [removed] = newRules.splice(oldIndex, 1);
                newRules.splice(newIndex, 0, removed);
                onDedupeRulesChange(newRules);
              }
            };

            return (
              <div className="space-y-4 py-4">
                {dedupeRules.length === 0 && (
                  <div className="p-4 bg-warning/10 border border-warning/20 rounded-md">
                    <p className="text-sm text-warning-foreground">
                      You must have at least one rule for picking the winning record when duplicates are found.
                    </p>
                  </div>
                )}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]"></TableHead>
                      <TableHead>Rule</TableHead>
                      <TableHead className="w-[50px] text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleAddRule}
                          className="h-8 w-8 p-0"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dedupeRules.map((rule, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              disabled={index === 0}
                              onClick={() => handleReorderRules(index, index - 1)}
                            >
                              <ChevronUp className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              disabled={index === dedupeRules.length - 1}
                              onClick={() => handleReorderRules(index, index + 1)}
                            >
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Combobox
                            options={ruleOptions}
                            value={ruleToOptionValue(rule)}
                            onValueChange={(value) => handleUpdateRule(index, value)}
                            placeholder="Select rule..."
                            emptyText="No rules available."
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveRule(index)}
                            className="h-8 w-8 p-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            );
          })()}
          </DialogBody>
          <DialogFooter>
            <Button onClick={() => setTiebreakerModalOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

